import React, { useMemo } from 'react';
import { Modal, Tag, Space, Typography, Card, Row, Col, Descriptions } from 'antd';
import { 
  HistoryOutlined, 
  EnvironmentOutlined, 
  UserOutlined,
  CalendarOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import { DataTable } from '../../ui';
import dayjs from 'dayjs';
import { RouteEvent } from '../../types';
import { useGetRouteEventsQuery } from '../../store/api/routesApi';
import { formatUserDisplayName } from '../../utils/userDisplayName';

const { Text } = Typography;

interface RouteEventsModalProps {
  visible: boolean;
  routeId: number;
  onClose: () => void;
  title?: string;
}

// Переводы типов событий
const eventTypeLabels = {
  'created': 'Создан',
  'updated': 'Обновлен',
  'status_changed': 'Смена статуса',
  'loading_started': 'Начало погрузки',
  'loading_completed': 'Погрузка завершена',
  'unloading_started': 'Начало выгрузки',
  'unloading_completed': 'Выгрузка завершена',
  'departed': 'Отправление',
  'arrived': 'Прибытие',
  'location_updated': 'Обновление местоположения',
  'document_received': 'Документ получен',
  'document_signed': 'Документ подписан',
  'payment_received': 'Оплата получена',
  'issue_detected': 'Обнаружена проблема',
  'issue_resolved': 'Проблема решена',
  STATUS_CHANGED: 'Смена статуса',
  RESOURCE_CHANGED: 'Смена ресурсов',
  APPLICATION_INJECTED: 'Добавлена заявка',
  ROUTE_CREATED: 'Маршрут создан',
  REOPENED: 'Маршрут открыт повторно'
};

// Цвета типов событий
const eventTypeColors = {
  'created': 'green',
  'updated': 'blue',
  'status_changed': 'orange',
  'loading_started': 'cyan',
  'loading_completed': 'green',
  'unloading_started': 'purple',
  'unloading_completed': 'green',
  'departed': 'blue',
  'arrived': 'green',
  'location_updated': 'geekblue',
  'document_received': 'lime',
  'document_signed': 'green',
  'payment_received': 'gold',
  'issue_detected': 'red',
  'issue_resolved': 'green',
  STATUS_CHANGED: 'orange',
  RESOURCE_CHANGED: 'blue',
  APPLICATION_INJECTED: 'cyan',
  ROUTE_CREATED: 'green',
  REOPENED: 'gold'
};

// Иконки типов событий
const eventTypeIcons = {
  'created': <InfoCircleOutlined />,
  'updated': <InfoCircleOutlined />,
  'status_changed': <HistoryOutlined />,
  'loading_started': <EnvironmentOutlined />,
  'loading_completed': <EnvironmentOutlined />,
  'unloading_started': <EnvironmentOutlined />,
  'unloading_completed': <EnvironmentOutlined />,
  'departed': <EnvironmentOutlined />,
  'arrived': <EnvironmentOutlined />,
  'location_updated': <EnvironmentOutlined />,
  'document_received': <InfoCircleOutlined />,
  'document_signed': <InfoCircleOutlined />,
  'payment_received': <InfoCircleOutlined />,
  'issue_detected': <InfoCircleOutlined />,
  'issue_resolved': <InfoCircleOutlined />
};

const RouteEventsModal: React.FC<RouteEventsModalProps> = ({
  visible,
  routeId,
  onClose,
  title = 'История событий маршрута'
}) => {
  // Получаем события маршрута
  const { data: events, isLoading } = useGetRouteEventsQuery(routeId, {
    skip: !routeId
  });

  // Колонки таблицы
  const columns: ColumnsType<RouteEvent> = useMemo(() => [
    {
      title: '№',
      key: 'index',
      width: 60,
      render: (_, __, index) => index + 1
    },
    {
      title: 'Тип события',
      dataIndex: 'eventType',
      key: 'eventType',
      width: 180,
      render: (_: any, record) => (
        <Space size="small">
          {eventTypeIcons[record.eventType as keyof typeof eventTypeIcons] || <InfoCircleOutlined />}
          <Tag color={eventTypeColors[record.eventType as keyof typeof eventTypeColors] || 'default'}>
            {eventTypeLabels[record.eventType as keyof typeof eventTypeLabels] || record.eventType}
          </Tag>
        </Space>
      )
    },
    {
      title: 'Описание',
      dataIndex: 'description',
      key: 'description',
      width: 300,
      render: (description: string) => (
        <div style={{ fontSize: '12px', lineHeight: '1.4' }}>
          {description || '—'}
        </div>
      )
    },
    {
      title: 'Местоположение',
      key: 'location',
      width: 200,
      render: (_, record) => {
        if (!record.locationLat || !record.locationLng) return '—';
        
        return (
          <div style={{ fontSize: '11px' }}>
            <div>
              <EnvironmentOutlined style={{ marginRight: 4 }} />
              Координаты:
            </div>
            <div style={{ color: '#666' }}>
              {Number(record.locationLat).toFixed(6)}, {Number(record.locationLng).toFixed(6)}
            </div>
          </div>
        );
      }
    },
    {
      title: 'Дата события',
      dataIndex: 'eventDate',
      key: 'eventDate',
      width: 150,
      render: (eventDate: string) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>
            {dayjs(eventDate).format('DD.MM.YYYY')}
          </div>
          <div style={{ fontSize: '11px', color: '#666' }}>
            {dayjs(eventDate).format('HH:mm:ss')}
          </div>
        </div>
      ),
      sorter: (a, b) => dayjs(a.eventDate).unix() - dayjs(b.eventDate).unix(),
      defaultSortOrder: 'descend'
    },
    {
      title: 'Дата записи',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (createdAt: string) => (
        <div>
          <div>{dayjs(createdAt).format('DD.MM.YYYY')}</div>
          <div style={{ fontSize: '11px', color: '#666' }}>
            {dayjs(createdAt).format('HH:mm:ss')}
          </div>
        </div>
      )
    },
    {
      title: 'Записал',
      key: 'createdByUser',
      width: 120,
      render: (_, record) => (
        <div style={{ fontSize: '12px' }}>
          <UserOutlined style={{ marginRight: 4 }} />
          {formatUserDisplayName(record.createdByUser)}
        </div>
      )
    }
  ], []);

  // Статистика событий
  const eventStats = useMemo(() => {
    if (!events) return null;
    
    const stats = {
      total: events.length,
      byType: {} as Record<string, number>,
      byDate: {} as Record<string, number>
    };
    
    events.forEach(event => {
      // Подсчет по типам
      stats.byType[event.eventType] = (stats.byType[event.eventType] || 0) + 1;
      
      // Подсчет по датам
      const dateKey = dayjs(event.eventDate).format('DD.MM.YYYY');
      stats.byDate[dateKey] = (stats.byDate[dateKey] || 0) + 1;
    });
    
    return stats;
  }, [events]);

  return (
    <Modal
      title={title}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={1400}
      destroyOnHidden
      maskClosable={false}
    >
      <Row gutter={16}>
        <Col span={16}>
          <Card title="История событий" size="small">
            <DataTable
              columns={columns}
              dataSource={events || []}
              loading={isLoading}
              rowKey="id"
              size="small"
              pagination={{
                pageSize: 15,
                showSizeChanger: true,
                showTotal: (total) => `Всего событий: ${total}`,
                showQuickJumper: true
              }}
              scroll={{ x: 'max-content' }}
            />
          </Card>
        </Col>
        
        <Col span={8}>
          <Card title="Статистика событий" size="small" style={{ marginBottom: 16 }}>
            {eventStats && (
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Всего событий">
                  <Text strong style={{ color: '#1890ff' }}>{eventStats.total}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Уникальных типов">
                  <Text strong>{Object.keys(eventStats.byType).length}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Дней с событиями">
                  <Text strong>{Object.keys(eventStats.byDate).length}</Text>
                </Descriptions.Item>
              </Descriptions>
            )}
          </Card>

          <Card title="Типы событий" size="small" style={{ marginBottom: 16 }}>
            {eventStats?.byType && (
              <div>
                {Object.entries(eventStats.byType)
                  .sort(([,a], [,b]) => b - a)
                  .map(([type, count]) => (
                    <div key={type} style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      marginBottom: 8,
                      padding: '4px 8px',
                      backgroundColor: '#f5f5f5',
                      borderRadius: 4
                    }}>
                      <span style={{ fontSize: '12px' }}>
                        {eventTypeLabels[type as keyof typeof eventTypeLabels] || type}
                      </span>
                      <Tag color={eventTypeColors[type as keyof typeof eventTypeColors] || 'default'}>
                        {count}
                      </Tag>
                    </div>
                  ))}
              </div>
            )}
          </Card>

          <Card title="Активность по дням" size="small">
            {eventStats?.byDate && (
              <div>
                {Object.entries(eventStats.byDate)
                  .sort(([a], [b]) => dayjs(b, 'DD.MM.YYYY').unix() - dayjs(a, 'DD.MM.YYYY').unix())
                  .slice(0, 7)
                  .map(([date, count]) => (
                    <div key={date} style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      marginBottom: 8,
                      padding: '4px 8px',
                      backgroundColor: '#f0f9ff',
                      borderRadius: 4
                    }}>
                      <span style={{ fontSize: '12px' }}>
                        <CalendarOutlined style={{ marginRight: 4 }} />
                        {date}
                      </span>
                      <Tag color="blue">{count}</Tag>
                    </div>
                  ))}
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </Modal>
  );
};

export default RouteEventsModal;

